
import React, { useState } from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Lightbulb, Send, Sparkles, Image } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface UniversalPromptProps {
  title: string;
  description: string;
  placeholder: string;
  examples: string[];
  loading?: boolean;
  showImageUpload?: boolean;
  imageSrc?: string;
  onSubmit: (prompt: string) => void;
  onImageUpload?: (file: File) => void;
}

const UniversalPrompt: React.FC<UniversalPromptProps> = ({
  title,
  description,
  placeholder,
  examples,
  loading = false,
  showImageUpload = false,
  imageSrc,
  onSubmit,
  onImageUpload
}) => {
  const [prompt, setPrompt] = useState('');

  const handleSubmit = () => {
    if (!prompt.trim()) {
      toast.error("Empty prompt", {
        description: "Please enter your prompt"
      });
      return;
    }
    onSubmit(prompt);
  };

  const handleExampleClick = (example: string) => {
    setPrompt(example);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && onImageUpload) {
      onImageUpload(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">{title}</h2>
        <p className="text-muted-foreground">{description}</p>
      </div>

      <Textarea
        placeholder={placeholder}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={6}
        className="min-h-[120px] transition bg-card-bg border-border/50 rounded-xl focus-visible:ring-primary"
      />

      {showImageUpload && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Image className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium">Add a screenshot or image</p>
          </div>
          
          {imageSrc ? (
            <div className="relative w-full max-w-xs mx-auto">
              <AspectRatio ratio={16 / 9} className="bg-muted rounded-md overflow-hidden">
                <img
                  src={imageSrc}
                  alt="Uploaded content"
                  className="w-full h-full object-cover"
                />
              </AspectRatio>
              <Button 
                variant="outline" 
                size="sm" 
                className="absolute top-2 right-2" 
                onClick={() => onImageUpload && onImageUpload(new File([], ''))}
              >
                Change
              </Button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full max-w-xs mx-auto h-32 rounded-lg border-2 border-dashed border-muted-foreground/25 cursor-pointer hover:bg-muted/50 transition-all">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Image className="w-8 h-8 mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click to upload an image</p>
              </div>
              <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
            </label>
          )}
        </div>
      )}

      {examples && examples.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium">Try these examples:</p>
          </div>
          <div className="flex flex-col gap-2">
            {examples.map((example, index) => (
              <Button 
                key={index} 
                variant="outline" 
                onClick={() => handleExampleClick(example)}
                className="justify-start text-left h-auto py-2 px-3 bg-card-bg hover:bg-card-hover border-border/50 transition-colors shadow-sm"
              >
                <span className="line-clamp-1">{example}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      <Button 
        onClick={handleSubmit} 
        className="w-full"
        size="lg"
        disabled={loading}
      >
        {loading ? (
          <>
            <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
            Processing...
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            Submit
          </>
        )}
      </Button>
    </div>
  );
};

export default UniversalPrompt;
